package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.ui.util.UiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.error_no_workplace_selected
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.AvailabilityRequest
import com.evoapps.evoshift.domain.model.Roster
import com.evoapps.evoshift.domain.model.RosterStatus
import com.evoapps.evoshift.domain.model.WeeklyAvailabilityTemplate
import com.evoapps.evoshift.domain.usecase.GetAvailabilityRequestUseCase
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.availability.GetWeeklyAvailabilityTemplatesUseCase
import com.evoapps.evoshift.domain.usecase.roster.GetRosterSummaryUseCase
import com.evoapps.evoshift.session.WorkplaceSessionManager
import com.evoapps.evoshift.ui.util.toUiText
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class AvailabilityUiState(
    val isLoading: Boolean = true,
    val currentWorkplaceId: String? = null,
    val openRosters: List<Roster> = emptyList(),
    val pastRosters: List<Roster> = emptyList(),
    val requests: Map<String, AvailabilityRequest> = emptyMap(), // Key: rosterId
    val templates: List<WeeklyAvailabilityTemplate> = emptyList(),
    val error: UiText? = null
)

class AvailabilityViewModel(
    private val getRosterSummaryUseCase: GetRosterSummaryUseCase,
    private val getAvailabilityRequestUseCase: GetAvailabilityRequestUseCase,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val getWeeklyAvailabilityTemplatesUseCase: GetWeeklyAvailabilityTemplatesUseCase,
    private val sessionManager: WorkplaceSessionManager
) : ViewModel() {

    private val _uiState = MutableStateFlow(AvailabilityUiState())
    val uiState: StateFlow<AvailabilityUiState> = _uiState.asStateFlow()
    private val dataJob = JobSlot()

    init {
        observeSession()
    }

    private fun observeSession() {
        viewModelScope.launch {
            sessionManager.currentWorkplaceId.collectLatest { workplaceId ->
                if (workplaceId != null) {
                    loadData(workplaceId)
                } else {
                    _uiState.update { it.copy(isLoading = false, error = UiText.Resource(Res.string.error_no_workplace_selected)) }
                }
            }
        }
    }

    private fun loadData(workplaceId: String) {
        dataJob.launch(viewModelScope) {
            _uiState.update {
                // Another workplace's rosters are cleared; for the same one the list stays while it refreshes.
                if (it.currentWorkplaceId != workplaceId) {
                    it.copy(
                        isLoading = true,
                        currentWorkplaceId = workplaceId,
                        openRosters = emptyList(),
                        pastRosters = emptyList(),
                        requests = emptyMap(),
                        templates = emptyList()
                    )
                } else {
                    it.copy(isLoading = it.openRosters.isEmpty() && it.pastRosters.isEmpty())
                }
            }

            val user = getCurrentUserUseCase().first()
            val templates = if (user != null) {
                getWeeklyAvailabilityTemplatesUseCase(
                    user.uid,
                    workplaceId
                ).getOrDefault(emptyList())
            } else emptyList()

            getRosterSummaryUseCase(workplaceId)
                // SH-188: a failed listener leaves the screen with a message, not an ended coroutine.
                .catch { e -> _uiState.update { it.copy(isLoading = false, error = e.toUiText()) } }
                .collect { rosters ->
                // Requirement: show only rosters for which the availability request has been sent
                val rostersWithRequest = rosters.filter { it.availabilityRequestId != null }

                // Include both SKELETON and DRAFT as "Open" requests for employees
                val open = rostersWithRequest.filter {
                    it.status == RosterStatus.DRAFT || it.status == RosterStatus.SKELETON
                }
                
                val past = rostersWithRequest.filter {
                    it.status == RosterStatus.PUBLISHED ||
                            it.status == RosterStatus.ARCHIVED ||
                            it.status == RosterStatus.LOCKED
                }.sortedByDescending { it.year * 100 + it.month }

                val requestsMap = mutableMapOf<String, AvailabilityRequest>()
                for (roster in rostersWithRequest) {
                    roster.availabilityRequestId?.let { requestId ->
                        getAvailabilityRequestUseCase(requestId).onSuccess { request ->
                            requestsMap[roster.id] = request
                        }
                    }
                }

                _uiState.update {
                    it.copy(
                        isLoading = false,
                        openRosters = open,
                        pastRosters = past,
                        requests = requestsMap,
                        templates = templates
                    )
                }
            }
        }
    }

    private var shownOnce = false

    /**
     * The availability screen is shown again (e.g. back from creating a weekly template): its
     * templates are re-read; the first showing is the initial load.
     */
    fun onScreenShown() {
        if (!shownOnce) { shownOnce = true; return }
        val workplaceId = _uiState.value.currentWorkplaceId ?: return
        viewModelScope.launch {
            val user = getCurrentUserUseCase().first() ?: return@launch
            getWeeklyAvailabilityTemplatesUseCase(user.uid, workplaceId).onSuccess { templates ->
                if (_uiState.value.currentWorkplaceId == workplaceId) _uiState.update { it.copy(templates = templates) }
            }
        }
    }
}

